import { Coins } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/useAuth';
import { useBillingBalance } from '@core/billing/useBillingBalance';

/**
 * Phase 8 · C-12 — Topbar pill showing remaining AI credits.
 *
 * Wired to `useBillingBalance()`:
 *   - balance ≥ 100 → emerald
 *   - balance 1–99  → amber
 *   - balance 0     → red
 *   - error         → muted "—", no crash
 */
interface TopbarCreditsPillProps {
  /** Optional click handler. Defaults to navigating to `/settings/billing`. */
  onClick?: () => void;
}

type Tier = 'healthy' | 'low' | 'empty' | 'error' | 'loading';

function tierFor(isError: boolean, isLoading: boolean, balance: number): Tier {
  if (isError) return 'error';
  if (isLoading) return 'loading';
  if (balance <= 0) return 'empty';
  if (balance < 100) return 'low';
  return 'healthy';
}

const TIER_STYLE: Record<Tier, { bg: string; fg: string }> = {
  healthy: { bg: 'var(--ggj-accent-soft)', fg: 'var(--ggj-accent-hover)' },
  low:     { bg: '#FEF3C7',               fg: '#92400E' },
  empty:   { bg: '#FEE2E2',               fg: '#991B1B' },
  error:   { bg: 'rgba(148, 163, 184, 0.18)', fg: 'var(--vscode-text-secondary, #64748B)' },
  loading: { bg: 'var(--ggj-accent-soft)', fg: 'var(--ggj-accent-hover)' },
};

export function TopbarCreditsPill({ onClick }: TopbarCreditsPillProps = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.orgId ?? undefined;
  const { balance, isLoading, isError } = useBillingBalance(orgId);

  const tier = tierFor(isError, isLoading, balance);
  const { bg, fg } = TIER_STYLE[tier];

  const label =
    tier === 'error'
      ? '—'
      : tier === 'loading'
        ? '…'
        : `${balance.toLocaleString()} credits`;

  const handleClick = () => {
    if (onClick) onClick();
    else navigate('/settings/billing');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={tier === 'error' ? 'Billing info unavailable' : label}
      aria-label={tier === 'error' ? 'Billing info unavailable' : label}
      className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium transition-opacity hover:opacity-90"
      style={{ backgroundColor: bg, color: fg }}
    >
      <Coins className="h-3.5 w-3.5" aria-hidden />
      <span className="hidden sm:inline">{label}</span>
      {tier !== 'error' && tier !== 'loading' ? (
        <span className="sm:hidden">{balance.toLocaleString()}</span>
      ) : (
        <span className="sm:hidden">{label}</span>
      )}
    </button>
  );
}
